/* DELIVERY ESTIMATE (Blanc Space custom)
 *
 * Upgrades the server-rendered working-day fallback in
 * `snippets/product-delivery-estimate.liquid` into calendar dates, plus the
 * same-day-dispatch countdown.
 *
 * All date maths happens on UTC-midnight Dates seeded from the SHOP's wall
 * clock (not the shopper's): the 7am cutoff is a UK warehouse fact, so a
 * shopper in Los Angeles must see the same dispatch day a shopper in Leeds
 * does.
 *
 * Two different calendars, deliberately:
 *   - WORKING days (Mon-Fri) move the dispatch day, because the warehouse is
 *     shut at weekends.
 *   - DELIVERY days (Mon-Sat) move the transit, because carriers deliver on
 *     Saturdays. Counting transit in working days silently turned a real
 *     Saturday arrival into the following Monday.
 * Both subtract the configured holiday list — leaving it empty is a
 * correctness gap around bank holidays, not a styling one, so keep it filled
 * in Theme settings > Cart.
 *
 * PRE-ORDERS (2026-09-14): stock state is decided by the SERVER, never here.
 * snippets/buy-buttons.liquid re-renders on every variant change and exposes
 * `data-bs-stock` (instock | backorder | soldout), the blank's ships-from date,
 * the reassurance note and the in-stock sibling to steer to. This element is
 * outside the re-rendered blocks, so it reads those attributes after each
 * `variant:change` and redraws: dispatch becomes the ships-from day (rolled to
 * a working day) and the nudge offers the sibling with today's delivery dates.
 */
(() => {
	const DAY_MS = 86400000;

	class DeliveryEstimate extends HTMLElement {
		connectedCallback() {
			this.textEl = this.querySelector('[data-delivery-text]');
			this.stockEl = this.querySelector('[data-delivery-stock]');
			this.cutoffEl = this.querySelector('[data-delivery-cutoff]');
			this.nudgeEl = this.querySelector('[data-delivery-nudge]');
			this.transitMin = Number(this.dataset.transitMin) || 0;
			this.transitMax = Number(this.dataset.transitMax) || 0;
			this.cutoffHour = Number(this.dataset.cutoffHour);
			if (!Number.isFinite(this.cutoffHour)) this.cutoffHour = 7;
			this.timezone = this.dataset.timezone || 'Europe/London';
			this.fallbackLabel = this.dataset.fallbackLabel || 'Ships in 2–3 weeks';
			this.holidays = new Set(
				(this.dataset.holidays || '')
					.split(',')
					.map((value) => value.trim())
					.filter(Boolean)
			);

			this.onVariantChange = (event) => {
				const variant = event.detail && event.detail.variant;
				if (!variant) return;
				this.readServerState(event.detail.formId, variant);
				this.render();
			};
			document.addEventListener('variant:change', this.onVariantChange);

			if (this.nudgeEl) {
				this.onNudgeClick = () => this.switchToNudge();
				this.nudgeEl.addEventListener('click', this.onNudgeClick);
			}

			// Initial state from the server render.
			this.available = !this.hasAttribute('data-unavailable');
			this.backorder = this.hasAttribute('data-backorder');
			// restock (2026-09-17): pre-order dates, In Stock wording, no nudge.
			this.restock = this.hasAttribute('data-restock');
			this.shipsFrom = this.dataset.shipsFrom || '';
			this.note = this.dataset.note || '';
			this.nudge = this.nudgeEl && this.nudgeEl.dataset.nudgeCase
				? { caseType: this.nudgeEl.dataset.nudgeCase, colour: this.nudgeEl.dataset.nudgeColour || '' }
				: null;
			this.render();
			// Minute granularity is enough for an "order within 4h 12m" line.
			this.timer = setInterval(() => this.render(), 30000);
		}

		disconnectedCallback() {
			clearInterval(this.timer);
			document.removeEventListener('variant:change', this.onVariantChange);
			if (this.nudgeEl && this.onNudgeClick) this.nudgeEl.removeEventListener('click', this.onNudgeClick);
		}

		/* The buy button block has already been swapped by the time
		   variant:change fires (product:rerender runs first), so its data
		   attributes are the freshest truth. Fall back to variant.available. */
		readServerState(formRef, variant) {
			// The theme passes the FORM ELEMENT as `detail.formId`, not a string
			// (caught live 2026-09-14: the id lookup never matched and every switch
			// fell back to "available = In Stock"). Accept either, then fall back
			// to the main buy box, which is the only one carrying data-bs-stock.
			let form = formRef;
			if (typeof formRef === 'string') form = document.getElementById(formRef) || document.forms[formRef] || null;
			const formId = form && form.id ? form.id : typeof formRef === 'string' ? formRef : '';
			let buyButtons = formId ? document.querySelector(`buy-buttons[form="${formId}"][data-bs-stock]`) : null;
			if (!buyButtons) buyButtons = document.querySelector('.product-info__buy-buttons buy-buttons[data-bs-stock]');
			if (buyButtons) {
				const state = buyButtons.dataset.bsStock;
				this.available = state === 'instock' || state === 'backorder' || state === 'restock';
				this.backorder = state === 'backorder';
				this.restock = state === 'restock';
				this.shipsFrom = buyButtons.dataset.bsShips || '';
				this.note = buyButtons.dataset.bsNote || '';
				this.nudge = buyButtons.dataset.bsNudgeCase
					? { caseType: buyButtons.dataset.bsNudgeCase, colour: buyButtons.dataset.bsNudgeColour || '' }
					: null;
				return;
			}
			this.available = variant.available !== false;
			this.backorder = false;
			this.restock = false;
			this.shipsFrom = '';
			this.note = '';
			this.nudge = null;
		}

		/* The shop's own wall clock, decomposed. hourCycle h23 avoids the "24"
		   that hour12:false returns at midnight in some engines. */
		shopNow() {
			const parts = new Intl.DateTimeFormat('en-GB', {
				timeZone: this.timezone,
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				hourCycle: 'h23',
			}).formatToParts(new Date());
			const value = (type) => Number(parts.find((part) => part.type === type)?.value);
			return {
				date: new Date(Date.UTC(value('year'), value('month') - 1, value('day'))),
				hour: value('hour'),
				minute: value('minute'),
			};
		}

		isHoliday(date) {
			return this.holidays.has(date.toISOString().slice(0, 10));
		}

		// Warehouse open: Mon-Fri.
		isWorkingDay(date) {
			const day = date.getUTCDay();
			return day !== 0 && day !== 6 && !this.isHoliday(date);
		}

		// Carrier delivers: Mon-Sat.
		isDeliveryDay(date) {
			return date.getUTCDay() !== 0 && !this.isHoliday(date);
		}

		nextDay(date, predicate) {
			let next = new Date(date.getTime() + DAY_MS);
			while (!predicate(next)) next = new Date(next.getTime() + DAY_MS);
			return next;
		}

		nextWorkingDay(date) {
			return this.nextDay(date, (candidate) => this.isWorkingDay(candidate));
		}

		// Same day if it is a working day, else the next one (for a ships-from date).
		onOrAfterWorkingDay(date) {
			return this.isWorkingDay(date) ? date : this.nextWorkingDay(date);
		}

		addDeliveryDays(date, count) {
			let result = date;
			for (let i = 0; i < count; i += 1) result = this.nextDay(result, (candidate) => this.isDeliveryDay(candidate));
			return result;
		}

		parseIso(value) {
			const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
			if (!match) return null;
			return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
		}

		/* Composed by hand rather than taken from a locale pattern. Intl has no
		   "weekday + day, month omitted" pattern, and asking for one returns
		   whatever that locale does with the leftovers ("15 Tue" under `en`).
		   Names still come from Intl so Markets translations survive; only the
		   day/month ORDER is decided here, from how the locale itself orders a
		   known date. */
		locale() {
			const lang = document.documentElement.lang;
			// The theme ships bare `en`, which Intl resolves to US ordering
			// ("Fri Sep 18") on a store that writes British English everywhere
			// else. A real regional tag is honoured as-is.
			if (!lang || lang.toLowerCase() === 'en') return 'en-GB';
			return lang;
		}

		monthFirst() {
			const parts = new Intl.DateTimeFormat(this.locale(), {
				timeZone: 'UTC',
				day: 'numeric',
				month: 'short',
			}).formatToParts(new Date(Date.UTC(2026, 0, 2)));
			return parts.findIndex((part) => part.type === 'month') < parts.findIndex((part) => part.type === 'day');
		}

		formatDate(date, withMonth) {
			const name = (options) =>
				new Intl.DateTimeFormat(this.locale(), { timeZone: 'UTC', ...options }).format(date);
			const weekday = name({ weekday: 'short' });
			const day = name({ day: 'numeric' });
			if (!withMonth) return `${weekday} ${day}`;
			const month = name({ month: 'short' });
			return this.monthFirst() ? `${weekday} ${month} ${day}` : `${weekday} ${day} ${month}`;
		}

		formatRange(from, to) {
			const sameMonth = from.getUTCMonth() === to.getUTCMonth() && from.getUTCFullYear() === to.getUTCFullYear();
			if (from.getTime() === to.getTime()) return this.formatDate(to, true);
			return `${this.formatDate(from, !sameMonth)} – ${this.formatDate(to, true)}`;
		}

		rangeFrom(dispatch) {
			return this.formatRange(
				this.addDeliveryDays(dispatch, this.transitMin),
				this.addDeliveryDays(dispatch, this.transitMax)
			);
		}

		render() {
			if (!this.textEl) return;

			if (!this.available) {
				this.stockEl.textContent = 'Out of stock';
				this.textEl.textContent = 'Not available for this phone model.';
				this.setAttribute('data-unavailable', '');
				this.removeAttribute('data-backorder');
				this.cutoffEl.hidden = true;
				this.renderNudge(null);
				return;
			}

			this.removeAttribute('data-unavailable');

			const now = this.shopNow();
			const beforeCutoff = this.isWorkingDay(now.date) && now.hour < this.cutoffHour;

			// ONE dispatch day — today if the cutoff is still catchable, else the
			// next working day — then the fastest service's own spread on top.
			// Stacking a worst-case dispatch onto a worst-case transit double
			// counts the same uncertainty and reads slower than the business is.
			const dispatch = beforeCutoff ? now.date : this.nextWorkingDay(now.date);

			if (this.backorder || this.restock) {
				this.toggleAttribute('data-backorder', this.backorder);
				this.toggleAttribute('data-restock', this.restock);
				this.stockEl.textContent = this.backorder ? 'Pre-order' : 'In Stock';
				const restock = this.parseIso(this.shipsFrom);
				if (restock) {
					// The blank's ships-from day, rolled to a working day, unless it
					// has already passed (stale entry) — then the normal dispatch day.
					const shipsFrom = this.onOrAfterWorkingDay(restock > dispatch ? restock : dispatch);
					this.textEl.innerHTML = `Estimated delivery <strong>${this.rangeFrom(shipsFrom)}</strong>`;
				} else {
					this.textEl.textContent = this.fallbackLabel;
				}
				// Only a note staff typed on the window entry; nothing by default.
				this.cutoffEl.textContent = this.note || '';
				this.cutoffEl.hidden = !this.note;
				this.renderNudge(dispatch);
				return;
			}

			this.removeAttribute('data-backorder');
			this.removeAttribute('data-restock');
			this.stockEl.textContent = 'In Stock';
			this.textEl.innerHTML = `Estimated delivery <strong>${this.rangeFrom(dispatch)}</strong>`;
			this.renderNudge(null);

			if (beforeCutoff) {
				const minutesLeft = (this.cutoffHour - now.hour) * 60 - now.minute;
				const hours = Math.floor(minutesLeft / 60);
				const minutes = minutesLeft % 60;
				const remaining = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
				this.cutoffEl.textContent = `Order within ${remaining} for same-day dispatch.`;
				this.cutoffEl.hidden = false;
			} else {
				// Clear as well as hide: a pre-order note may be sitting here from
				// the previous variant.
				this.cutoffEl.textContent = '';
				this.cutoffEl.hidden = true;
			}
		}

		/* "Want it sooner?" — the in-stock sibling the server found, with the
		   delivery window it would get if ordered now. Hidden when there is
		   nothing on the shelf for this phone. */
		renderNudge(dispatch) {
			if (!this.nudgeEl) return;
			if (!this.backorder || !this.nudge || !dispatch) {
				this.nudgeEl.hidden = true;
				return;
			}
			const label = `${this.nudge.caseType} ${this.nudge.colour}`.trim();
			this.nudgeEl.innerHTML =
				`Want it sooner? <strong>${label}</strong> is in stock for your phone` +
				`<span data-nudge-dates>, delivered ${this.rangeFrom(dispatch)}</span>. ` +
				'<span class="delivery-estimate__nudge-action">Switch</span>';
			this.nudgeEl.dataset.nudgeCase = this.nudge.caseType;
			this.nudgeEl.dataset.nudgeColour = this.nudge.colour;
			this.nudgeEl.hidden = false;
		}

		/* Selects the sibling through the picker's own radios, so the closest-
		   match snap, partial re-render and variant:change all run exactly as a
		   shopper's tap would (no page load). */
		switchToNudge() {
			if (!this.nudge) return;
			const picker = document.querySelector('variant-picker.variant-picker-cases');
			const matrixEl = picker && picker.querySelector('[data-vpc-matrix]');
			const form = picker && document.forms[picker.getAttribute('form-id')];
			if (!picker || !matrixEl || !form) return;
			let values;
			try {
				values = JSON.parse(matrixEl.textContent).values;
			} catch (error) {
				return;
			}
			const radios = Array.from(form.elements).filter((el) => el.matches && el.matches('input[data-vpc-position]'));
			const find = (position, name) =>
				radios.find((radio) => {
					const value = values[radio.value];
					return value && value[0] === position && value[1] === name;
				});
			const caseRadio = find(2, this.nudge.caseType);
			const colourRadio = this.nudge.colour ? find(3, this.nudge.colour) : null;
			if (caseRadio) caseRadio.checked = true;
			if (colourRadio) colourRadio.checked = true;
			const trigger = colourRadio || caseRadio;
			if (!trigger) return;
			window.bsTrack?.('backorder_steer', {
				reason: 'nudge',
				to_case: this.nudge.caseType,
				to_colour: this.nudge.colour || null,
			});
			trigger.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	if (!customElements.get('delivery-estimate')) {
		customElements.define('delivery-estimate', DeliveryEstimate);
	}
})();
